import { useState, useEffect } from 'react';
import { stagesApi, Stage } from '../services/stagesApi';
import { leadsApi, LeadWithAgent } from '../services/leadsApi';
import { trainingFlowsApi } from '../../agents/services/trainingFlowsApi';
import { agentsApi } from '../../agents/services/agentsApi';
import { FormSelect } from '../../../components/ui/select';
import FunnelColumn from '../components/FunnelColumn';
import AddStage from '../components/AddStage';
import { Loader2 } from 'lucide-react';

export default function FunnelPage() {
  const [leads, setLeads] = useState<LeadWithAgent[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [flows, setFlows] = useState<{ id: string; nombre: string }[]>([]);
  const [agents, setAgents] = useState<{ id: string; nombre: string }[]>([]);

  // Load filters
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [flowsData, agentsData] = await Promise.all([
          trainingFlowsApi.listFlows(),
          agentsApi.listAgents(),
        ]);
        setFlows(flowsData);
        setAgents(agentsData);
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await stagesApi.ensureDefaultStage();
        const [stagesData, leadsData] = await Promise.all([
          stagesApi.fetchStages(),
          leadsApi.getLeads(selectedFlowId || undefined, selectedAgentId || undefined),
        ]);
        setStages(stagesData);
        setLeads(leadsData);
      } catch (error) {
        console.error('Error loading funnel data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedFlowId, selectedAgentId]);

  const handleStageCreated = async () => {
    const stagesData = await stagesApi.fetchStages();
    setStages(stagesData);
  };

  const handleLeadUpdated = async () => {
    const leadsData = await leadsApi.getLeads(
      selectedFlowId || undefined,
      selectedAgentId || undefined
    );
    setLeads(leadsData);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-background">
      {/* Header */}
      <div className="bg-white border-b border-grey-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground font-montserrat">Funnel</h1>
          <div className="flex items-center gap-4">
            <div className="w-40">
              <FormSelect
                label=""
                value={selectedFlowId}
                onChange={(value) => setSelectedFlowId(value)}
                options={[
                  { value: '', label: 'Todos los flows' },
                  ...flows.map((f) => ({ value: f.id, label: f.nombre })),
                ]}
                placeholder="Flow"
                className="w-full"
              />
            </div>
            <div className="w-40">
              <FormSelect
                label=""
                value={selectedAgentId}
                onChange={(value) => setSelectedAgentId(value)}
                options={[
                  { value: '', label: 'Todos los agentes' },
                  ...agents.map((a) => ({ value: a.id, label: a.nombre })),
                ]}
                placeholder="Agente"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-grey-400" />
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-hidden px-4 py-4">
          <div className="flex gap-3 h-full min-w-min">
            {stages.map((stage) => {
              const stageLeads = leads.filter((lead) => lead.tags === stage.name);
              return (
                <FunnelColumn
                  key={stage.id}
                  stage={stage}
                  leads={stageLeads}
                  onLeadUpdated={handleLeadUpdated}
                />
              );
            })}
            <div className="w-[220px] flex-shrink-0">
              <AddStage onStageCreated={handleStageCreated} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
